/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import i18n, { formatDateTime as formatLocaleDateTime } from '../../i18n';
import MainLayout from '../../components/common/MainLayout';
import {
  useCreateSeasonMutation,
  useGetDebugTemplatesQuery,
  useGetSeasonAttemptsQuery,
  useGetSeasonLeaderboardQuery,
  useGetSeasonQuery,
  useListSeasonsQuery,
  useVerifySeasonMutation,
  useCloseSeasonMutation,
  type LeaderboardEntry,
  type SeasonAttempt,
  type SeasonSummary,
} from '../../api/seasonsAdminApi';

const { Text } = Typography;

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  return formatLocaleDateTime(value);
};

const modeTag = (mode: string) => (mode === 'blind'
  ? <Tag color="gold">{i18n.t('admin:seasons.mode.blind')}</Tag>
  : <Tag color="blue">{i18n.t('admin:seasons.mode.open')}</Tag>);

/** manifest типизирован как Record<string, unknown> — читаем скалярами, иначе '—'. */
const manifestScalar = (manifest: Record<string, unknown>, key: string): string | null => {
  const value = manifest[key];
  if (typeof value === 'string') return value || null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

const manifestObject = (manifest: Record<string, unknown>, key: string): Record<string, unknown> => {
  const value = manifest[key];
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
};

const hashCell = (value: string | null) => {
  if (!value) return <Text type="secondary">—</Text>;
  const shown = value.length > 20 ? `${value.slice(0, 16)}…` : value;
  return <Text code copyable={{ text: value }}>{shown}</Text>;
};

const attemptStatusTag = (attempt: SeasonAttempt) => {
  if (attempt.status === 'verified') return <Tag color="green">{i18n.t('admin:seasons.attemptStatus.verified')}</Tag>;
  if (attempt.status === 'rejected') return <Tag color="red">{i18n.t('admin:seasons.attemptStatus.rejected')}</Tag>;
  if (attempt.scoreSource === 'client') return <Tag color="gold">{i18n.t('admin:seasons.attemptStatus.provisional')}</Tag>;
  return <Tag>{i18n.t('admin:seasons.attemptStatus.submitted')}</Tag>;
};

const scoreCell = (score: SeasonAttempt['serverScore'] | SeasonAttempt['provisionalScore']) => {
  if (!score) return <Text type="secondary">—</Text>;
  return (
    <Space size={4}>
      <Tag color={score.scoreTier === 3 ? 'blue' : score.scoreTier === 2 ? 'red' : 'default'}>
        tier {score.scoreTier ?? '—'}
      </Tag>
      <Text>{score.passedCount ?? '—'}/{score.total ?? '—'}</Text>
    </Space>
  );
};

/** Развёрнутая панель одного сезона: лидерборд + попытки + верификация. */
const SeasonDetail: React.FC<{ season: SeasonSummary }> = ({ season }) => {
  const { t } = useTranslation('admin');
  const { data: board, isFetching: boardLoading } = useGetSeasonLeaderboardQuery(season.id);
  const { data: attemptsData, isFetching: attemptsLoading } = useGetSeasonAttemptsQuery(season.id);
  // Снапшот + манифест отдаёт только GET /seasons/:id — в списке их нет.
  const { data: frozen, isFetching: frozenLoading } = useGetSeasonQuery(season.id);
  const [verifySeason, { isLoading: verifying }] = useVerifySeasonMutation();
  const [closeSeason, { isLoading: closing }] = useCloseSeasonMutation();

  const manifest = (frozen?.season.manifest || season.manifest || {}) as Record<string, unknown>;
  const manifestWindow = manifestObject(manifest, 'window');
  const signature = frozen?.season.signature || season.signature || '';
  const snapshot = frozen?.season.template;

  const pendingCount = useMemo(
    () => (attemptsData?.attempts || []).filter(
      (a) => a.status === 'submitted' && a.scoreSource === 'client',
    ).length,
    [attemptsData],
  );

  const onVerify = async () => {
    try {
      const result = await verifySeason({ seasonId: season.id }).unwrap();
      message.success(
        t('seasons.messages.verifyResult', {
          checked: result.checked,
          verified: result.verified,
          rejected: result.rejected,
        }),
      );
    } catch (err: unknown) {
      const detail = (err as { data?: { message?: string } })?.data?.message;
      message.error(detail || t('seasons.messages.verifyFailed'));
    }
  };

  const onClose = async (force: boolean) => {
    try {
      const result = await closeSeason({ seasonId: season.id, force }).unwrap();
      const issued = result.placementsIssued?.length ?? 0;
      message.success(result.alreadyClosed
        ? t('seasons.messages.alreadyClosed')
        : t('seasons.messages.closed', {
          verified: result.verified ?? 0,
          abandoned: result.abandoned ?? 0,
          issued,
        }));
    } catch (err: unknown) {
      const data = (err as { data?: { message?: string; code?: string } })?.data;
      message.error(data?.code === 'SEASON_STILL_OPEN'
        ? t('seasons.messages.stillOpen')
        : data?.message || t('seasons.messages.closeFailed'));
    }
  };

  const closedAlready = season.status === 'closed';
  const windowPassed = new Date(season.closesAt).getTime() <= Date.now();

  const leaderboardColumns: ColumnsType<LeaderboardEntry> = [
    { title: '#', dataIndex: 'rank', width: 48 },
    {
      title: t('seasons.columns.player'),
      dataIndex: 'displayName',
      render: (name: string | null, row) => name || <Text type="secondary">{row.userId.slice(0, 8)}…</Text>,
    },
    {
      title: t('seasons.columns.score'),
      key: 'score',
      render: (_, row) => (
        <Space size={4}>
          <Tag color={row.scoreTier === 3 ? 'blue' : row.scoreTier === 2 ? 'red' : 'default'}>
            tier {row.scoreTier ?? '—'}
          </Tag>
          <Text>{row.passedCount ?? '—'}/{row.total ?? '—'}</Text>
          {row.primaryMetricKey ? (
            <Text type="secondary">
              {row.primaryMetricKey}: {row.primaryMetricValue ?? '—'}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: t('common:labels.status'),
      key: 'status',
      width: 150,
      render: (_, row) => (row.verified
        ? <Tag color="green">{t('seasons.entryStatus.verified')}</Tag>
        : row.provisional
          ? <Tooltip title={t('seasons.entryStatus.provisionalHint')}><Tag color="gold">{t('seasons.entryStatus.provisional')}</Tag></Tooltip>
          : <Tag>—</Tag>),
    },
    { title: t('seasons.columns.submittedAt'), dataIndex: 'submittedAt', render: formatDateTime, width: 170 },
  ];

  const attemptColumns: ColumnsType<SeasonAttempt> = [
    {
      title: t('seasons.columns.player'),
      dataIndex: 'displayName',
      render: (name: string | null, row) => name || <Text type="secondary">{row.userId.slice(0, 8)}…</Text>,
    },
    { title: t('common:labels.status'), key: 'status', width: 160, render: (_, row) => attemptStatusTag(row) },
    {
      title: t('seasons.columns.serverScore'),
      key: 'server',
      render: (_, row) => scoreCell(row.serverScore),
    },
    {
      title: t('seasons.columns.provisionalScore'),
      key: 'prov',
      render: (_, row) => scoreCell(row.provisionalScore),
    },
    { title: t('seasons.columns.events'), dataIndex: 'eventCount', width: 90 },
    { title: t('seasons.columns.attemptSubmittedAt'), dataIndex: 'submittedAt', render: formatDateTime, width: 170 },
    { title: t('seasons.columns.verifiedAt'), dataIndex: 'verifiedAt', render: formatDateTime, width: 170 },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Descriptions size="small" column={4}>
        <Descriptions.Item label={t('seasons.detail.template')}>{season.scenarioTemplateId} v{season.version}</Descriptions.Item>
        <Descriptions.Item label={t('seasons.detail.window')}>
          {formatDateTime(season.opensAt)} → {formatDateTime(season.closesAt)}
        </Descriptions.Item>
        <Descriptions.Item label={t('seasons.detail.attemptsPerUser')}>{season.attemptsLimit}</Descriptions.Item>
        <Descriptions.Item label={t('seasons.detail.engine')}>{season.engineVersion} / {season.scoringVersion}</Descriptions.Item>
      </Descriptions>

      <Space wrap>
        {season.verificationPolicy === 'lazy' && (
          <>
            <Button type="primary" onClick={onVerify} loading={verifying} disabled={pendingCount === 0 || closedAlready}>
              {t('seasons.verify.button', { count: pendingCount })}
            </Button>
            {pendingCount === 0 && <Text type="secondary">{t('seasons.verify.queueEmpty')}</Text>}
          </>
        )}
        {closedAlready ? (
          <Tag color="default">{t('seasons.close.closedAt', { date: formatDateTime(season.closedAt) })}</Tag>
        ) : (
          <Popconfirm
            title={windowPassed ? t('seasons.close.confirmTitle') : t('seasons.close.confirmForceTitle')}
            description={t('seasons.close.confirmDescription')}
            okText={t('common:actions.close')}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => onClose(!windowPassed)}
          >
            <Button danger={!windowPassed} loading={closing}>
              {windowPassed ? t('seasons.close.button') : t('seasons.close.forceButton')}
            </Button>
          </Popconfirm>
        )}
      </Space>

      <Card size="small" title={t('seasons.frozen.title')} loading={frozenLoading}>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('seasons.frozen.hashWarning')}
        />
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="templateHash" span={2}>
            {hashCell(manifestScalar(manifest, 'templateHash'))}
          </Descriptions.Item>
          <Descriptions.Item label="scoringVersion">
            {manifestScalar(manifest, 'scoringVersion') || <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="engineVersion">
            {manifestScalar(manifest, 'engineVersion') || <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label={t('seasons.frozen.manifestWindow')} span={2}>
            {formatDateTime(manifestScalar(manifestWindow, 'opensAt'))}
            {' → '}
            {formatDateTime(manifestScalar(manifestWindow, 'closesAt'))}
          </Descriptions.Item>
          <Descriptions.Item label={t('seasons.frozen.manifestAttempts')}>
            {manifestScalar(manifest, 'attemptsLimit') || <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label={t('seasons.frozen.signature')}>
            {hashCell(signature || null)}
          </Descriptions.Item>
        </Descriptions>
        <Collapse
          size="small"
          style={{ marginTop: 12 }}
          items={[{
            key: 'snapshot',
            label: t('seasons.frozen.snapshot'),
            children: (
              <>
                {season.mode === 'blind' && (
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    {t('seasons.frozen.blindNote')}
                  </Text>
                )}
                <pre
                  style={{
                    margin: 0,
                    maxHeight: 360,
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                >
                  {snapshot ? JSON.stringify(snapshot, null, 2) : '—'}
                </pre>
              </>
            ),
          }]}
        />
      </Card>

      <Card size="small" title={t('seasons.leaderboard.title')} loading={boardLoading}>
        <Table<LeaderboardEntry>
          rowKey={(row) => row.userId}
          columns={leaderboardColumns}
          dataSource={board?.entries || []}
          size="small"
          pagination={false}
          locale={{ emptyText: t('seasons.noAttempts') }}
        />
      </Card>

      <Card size="small" title={t('seasons.attempts.title')} loading={attemptsLoading}>
        <Table<SeasonAttempt>
          rowKey={(row) => row.id}
          columns={attemptColumns}
          dataSource={attemptsData?.attempts || []}
          size="small"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: t('seasons.noAttempts') }}
        />
      </Card>
    </Space>
  );
};

const AdminSeasonsPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const { data, isFetching, refetch } = useListSeasonsQuery();
  const { data: templatesData } = useGetDebugTemplatesQuery();
  const [createSeason, { isLoading: creating }] = useCreateSeasonMutation();
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const selectedMode = Form.useWatch('mode', form) as string | undefined;

  const seasons = data?.seasons || [];
  const templates = templatesData?.templates || [];

  // Бэкенд отвечает 400 на blind + lazy: лог слепого сезона копится на сервере.
  useEffect(() => {
    if (selectedMode === 'blind') {
      form.setFieldsValue({ verificationPolicy: 'inline' });
    }
  }, [selectedMode, form]);

  const onCreate = async () => {
    try {
      const values = await form.validateFields();
      const created = await createSeason({
        templateId: values.templateId,
        mode: values.mode,
        opensAt: values.window[0].toISOString(),
        closesAt: values.window[1].toISOString(),
        attemptsLimit: values.attemptsLimit,
        verificationPolicy: values.mode === 'blind' ? 'inline' : values.verificationPolicy,
      }).unwrap();
      message.success(t('seasons.messages.started', { id: created.id }));
      setCreateOpen(false);
      form.resetFields();
    } catch (err: unknown) {
      const detail = (err as { data?: { message?: string } })?.data?.message;
      if (detail) message.error(detail);
    }
  };

  const columns: ColumnsType<SeasonSummary> = [
    {
      title: t('seasons.columns.template'),
      dataIndex: 'scenarioTemplateId',
      render: (id: string, row) => {
        const template = templates.find(
          (tpl) => tpl.scenarioTemplateId === id || tpl.slug === id,
        );
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{template?.title || id}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{row.id}</Text>
          </Space>
        );
      },
    },
    {
      title: t('common:labels.status'),
      dataIndex: 'status',
      width: 100,
      render: (status: string) => <Tag color={status === 'open' ? 'green' : 'default'}>{status}</Tag>,
    },
    {
      title: t('seasons.columns.mode'),
      dataIndex: 'mode',
      width: 110,
      render: (mode: string) => modeTag(mode),
    },
    {
      title: t('seasons.columns.verification'),
      dataIndex: 'verificationPolicy',
      width: 130,
      render: (policy: string) => (policy === 'lazy'
        ? <Tag color="gold">{t('seasons.policy.lazy')}</Tag>
        : <Tag color="green">{t('seasons.policy.inline')}</Tag>),
    },
    { title: t('seasons.columns.opensAt'), dataIndex: 'opensAt', render: formatDateTime, width: 170 },
    { title: t('seasons.columns.closesAt'), dataIndex: 'closesAt', render: formatDateTime, width: 170 },
    { title: t('seasons.columns.attempts'), dataIndex: 'attemptsLimit', width: 90 },
  ];

  return (
    <MainLayout>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title={t('seasons.title')}
            extra={(
              <Space>
                <Button onClick={() => refetch()}>{t('common:actions.refresh')}</Button>
                <Button type="primary" onClick={() => setCreateOpen(true)}>{t('seasons.create.button')}</Button>
              </Space>
            )}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('seasons.intro')}
            />
            <Table<SeasonSummary>
              rowKey={(row) => row.id}
              columns={columns}
              dataSource={seasons}
              loading={isFetching}
              expandable={{
                expandedRowRender: (season) => <SeasonDetail season={season} />,
              }}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{ emptyText: t('seasons.empty') }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title={t('seasons.create.title')}
        open={createOpen}
        onOk={onCreate}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={creating}
        okText={t('seasons.create.ok')}
        cancelText={t('common:actions.cancel')}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            mode: 'open',
            attemptsLimit: 3,
            verificationPolicy: 'inline',
            window: [dayjs(), dayjs().add(14, 'day')],
          }}
        >
          <Form.Item
            name="templateId"
            label={t('seasons.form.template')}
            rules={[{ required: true, message: t('seasons.form.templateRequired') }]}
          >
            <Select
              placeholder={t('seasons.form.templatePlaceholder')}
              options={templates.map((tpl) => ({
                value: tpl.slug,
                label: t('seasons.form.templateOption', {
                  title: tpl.title,
                  slug: tpl.slug,
                  count: tpl.durationDays,
                }),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="mode"
            label={t('seasons.columns.mode')}
            tooltip={t('seasons.form.modeTooltip')}
          >
            <Select
              options={[
                { value: 'open', label: t('seasons.form.modeOpen') },
                { value: 'blind', label: t('seasons.form.modeBlind') },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="window"
            label={t('seasons.form.window')}
            rules={[{ required: true, message: t('seasons.form.windowRequired') }]}
          >
            <DatePicker.RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="attemptsLimit" label={t('seasons.form.attemptsLimit')}>
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="verificationPolicy"
            label={t('seasons.columns.verification')}
            tooltip={t('seasons.form.policyTooltip')}
          >
            <Select
              options={[
                { value: 'inline', label: t('seasons.form.policyInline') },
                {
                  value: 'lazy',
                  label: t('seasons.form.policyLazy'),
                  disabled: selectedMode === 'blind',
                },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default AdminSeasonsPage;
